import React from "react";
import { Trans, useTranslation } from "react-i18next";

import GhxpArrows from "./GhxpArrows";
import GhxpSelect from "./GhxpSelect";
import { CtaButton, Eyebrow, KineticTitle, SceneStage } from "./GhxpUi";
import { FORM_BACKDROPS } from "./ghxpAssets";
import { STEPS, validateFields } from "./ghxpFormConfig";
import useSceneVideo from "./useSceneVideo";
import privacyPolicy from "src/assets/politica_de_privacidade.pdf";

const PolicyLink = ({ children }) => (
  <a href={privacyPolicy} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const pad = (n) => String(n).padStart(2, "0");


function GhxpStep({
  step,
  stepIndex,
  totalSteps,
  formData,
  errors,
  isSending,
  submitError,
  shaking,
  near,
  active,
  settled,
  lowPower,
  portrait,
  onChange,
  onNext,
  onBack,
  onSkip,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const isLast = stepIndex === totalSteps - 1;
  const backdrop = FORM_BACKDROPS[stepIndex];
  const { videoRef, playing, onPlaying } = useSceneVideo({ active, settled, warm: near, enabled: !lowPower, variant: portrait });

  const handleSubmit = (event) => {
    event.preventDefault();
    onNext();
  };

  const renderControl = (field) => {
    const common = {
      id: `ghxp-${field.name}`,
      name: field.name,
      value: formData[field.name],
      "aria-invalid": !!errors[field.name],
      "aria-describedby": errors[field.name] ? `ghxp-${field.name}-error` : undefined,
      onChange: (e) => onChange(field.name, field.mask ? field.mask(e.target.value, lang) : e.target.value),
    };

    if (field.type === "select") {
      return (
        <GhxpSelect
          id={common.id}
          name={field.name}
          value={formData[field.name]}
          options={field.options.map((option) => ({ value: option.value, label: option.labelKey ? t(option.labelKey) : option.label }))}
          placeholder={t("ghxp_select_placeholder")}
          invalid={common["aria-invalid"]}
          describedBy={common["aria-describedby"]}
          labelId={`ghxp-${field.name}-label`}
          onChange={(value) => onChange(field.name, value)}
        />
      );
    }

    return (
      <input
        {...common}
        type={field.type}
        autoComplete={field.autoComplete}
        inputMode={typeof field.inputMode === "function" ? field.inputMode(lang) : field.inputMode}
        placeholder={field.placeholderKey ? t(field.placeholderKey) : undefined}
        maxLength={field.maxLength}
        min={field.min}
        max={typeof field.max === "function" ? field.max() : field.max}
      />
    );
  };

  // Campo válido (preenchido e sem erro): a seta dupla da marca aparece no fim da linha.
  const isValid = (name) => !!formData[name] && !errors[name] && !validateFields(formData, [name], lang)[name];


  return (
    <>
      <SceneStage shade="form" className="ghxpForm__stage">
        {/* Pôster (primeiro quadro) por baixo; o vídeo entra com fade quando começa a tocar. */}
        {near && <img className="ghxpStage__poster" src={portrait ? backdrop.posterTall : backdrop.poster} alt="" decoding="async" />}
        {!lowPower && (
          <video
            ref={videoRef}
            key={portrait ? "tall" : "wide"}
            className={`ghxpStage__video ${playing ? "is-playing" : ""}`}
            src={portrait ? backdrop.videoTall : backdrop.video}
            muted
            loop
            playsInline
            preload={near ? "auto" : "none"}
            onPlaying={onPlaying}
          />
        )}
      </SceneStage>

      {/* Coluna de marca: número da etapa em contorno, a frase da etapa e as quatro etapas (como os temas do Encontro) */}
      <aside className="ghxpForm__brand ghxpDepth" aria-hidden="true">
        <Eyebrow className="ghxpReveal">{t("ghxp_form_eyebrow")}</Eyebrow>
        <p className="ghxpForm__number ghxpReveal">
          <span>{pad(stepIndex + 1)}</span>
          <small>/{pad(totalSteps)}</small>
        </p>
        <KineticTitle as="p" text={t(`ghxp_step_quote_${stepIndex + 1}`)} className="ghxpForm__quote" />
        <ol className="ghxpForm__steps ghxpReveal">
          {STEPS.map((item, i) => (
            <li key={item.titleKey} className={i < stepIndex ? "is-done" : i === stepIndex ? "is-current" : ""}>
              <span className="ghxpForm__stepIndex">{pad(i + 1)}</span>
              <span className="ghxpForm__stepName">{t(item.titleKey)}</span>
              {i < stepIndex && <GhxpArrows variant="solid" size="sm" className="ghxpForm__stepCheck" />}
            </li>
          ))}
        </ol>
      </aside>

      <div className="ghxpFormSlide__scroll" data-scrollable="y">
        <form className={`ghxpStep ghxpDepth ${shaking ? "is-shaking" : ""}`} onSubmit={handleSubmit} noValidate>
          <div className="ghxpStep__progress ghxpReveal">
            <p className="ghxpStep__eyebrow">
              {t("ghxp_step_label", { current: pad(stepIndex + 1), total: pad(totalSteps) })}
              {step.optional && <span className="ghxpStep__optional">{t("ghxp_optional")}</span>}
            </p>
            <div className="ghxpStep__bar" aria-hidden="true">
              {Array.from({ length: totalSteps }, (_, i) => (
                <span key={i} className={i < stepIndex ? "is-done" : i === stepIndex ? "is-current" : ""} />
              ))}
            </div>
          </div>

          <KineticTitle text={t(step.titleKey)} className="ghxpStep__title" />
          <p className="ghxpStep__hint ghxpReveal">{t(`ghxp_step_hint_${stepIndex + 1}`)}</p>

          <div className={`ghxpStep__fields ghxpReveal ${step.wide ? "is-wide" : ""}`}>
            {step.fields.map((field) => (
              <div
                key={field.name}
                className={`ghxpField ${errors[field.name] ? "has-error" : ""} ${isValid(field.name) ? "is-valid" : ""}`}
              >
                <label htmlFor={`ghxp-${field.name}`} id={`ghxp-${field.name}-label`}>
                  {t(field.labelKey)}
                </label>
                <span className="ghxpField__control">
                  {renderControl(field)}
                  <span className="ghxpField__line" aria-hidden="true" />
                  <GhxpArrows variant="solid" size="sm" className="ghxpField__ok" />
                </span>
                <p className="ghxpField__error" id={`ghxp-${field.name}-error`} role="alert">
                  {errors[field.name] ? t(errors[field.name]) : ""}
                </p>
              </div>
            ))}
          </div>

          {submitError && (
            <p className="ghxpStep__submitError" role="alert">
              {t("ghxp_error_submit")}
            </p>
          )}

          <div className="ghxpStep__actions ghxpReveal">
            <button type="button" className="ghxpLink" onClick={onBack}>
              <GhxpArrows variant="solid" size="sm" className="ghxpLink__icon" />
              {t("ghxp_back")}
            </button>
            {step.optional && (
              <button type="button" className="ghxpTextLink ghxpStep__skip" onClick={onSkip} disabled={isSending}>
                {t("ghxp_skip")}
              </button>
            )}
            <CtaButton type="submit" className={isSending ? "is-sending" : ""} disabled={isSending}>
              {isLast ? t(isSending ? "ghxp_sending" : "ghxp_send") : t("ghxp_continue")}
            </CtaButton>
          </div>

          {isLast && (
            <p className="ghxpStep__note ghxpReveal">
              {/* A tag chama-se "policy": o Trans trata <link> como elemento vazio e o texto sumiria. */}
              <Trans i18nKey="ghxp_privacy" components={{ policy: <PolicyLink /> }} />
            </p>
          )}
        </form>
      </div>
    </>
  );
}

export default GhxpStep;
